import { EnvelopeSimple, GithubLogo, TwitterLogo, LinkedinLogo } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useContent } from "@/contexts/ContentContext";
import { useBranding } from "@/contexts/BrandingContext";
import BrandLogo from "@/components/BrandLogo";

const FALLBACK_LINKS = {
  Platform: [
    { label: "Features", href: "/features" },
    { label: "Live Data", href: "/live-data" },
    { label: "API Access", href: "/api-docs" },
    { label: "Documentation", href: "/api-docs" },
  ],
  Resources: [
    { label: "About", href: "/about" },
    { label: "Research Papers", href: "/research-papers" },
    { label: "Methodology", href: "/methodology" },
    { label: "Data Sources", href: "/data-sources" },
  ],
  Connect: [
    { label: "Contact Us", href: "/contact-us" },
    { label: "Newsletter", href: "/newsletter" },
    { label: "Partnerships", href: "/partnerships" },
    { label: "Careers", href: "/careers" },
  ],
};

function FooterLink({ link }) {
  const isInternal = link.href?.startsWith("/") && !link.href.startsWith("/#");
  const className = "font-body text-sm text-stone-400 hover:text-white transition-colors duration-300";
  const testId = `footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`;
  if (isInternal) {
    return (
      <Link to={link.href} data-testid={testId} className={className}>
        {link.label}
      </Link>
    );
  }
  return (
    <a href={link.href || "#"} data-testid={testId} className={className}>
      {link.label}
    </a>
  );
}

export default function Footer() {
  const { content } = useContent();
  const { branding } = useBranding();
  const footer = content?.footer || {};
  const tagline = footer.tagline || "Making critical earth science data accessible, understandable, and actionable for everyone on the planet.";

  const groups = {
    Platform: footer.platform_links || FALLBACK_LINKS.Platform,
    Resources: footer.resources_links || FALLBACK_LINKS.Resources,
    Connect: footer.connect_links || FALLBACK_LINKS.Connect,
  };

  const socials = [
    branding?.social_github  && { id: "github",   href: branding.social_github,  Icon: GithubLogo },
    branding?.social_twitter && { id: "twitter",  href: branding.social_twitter, Icon: TwitterLogo },
    branding?.social_linkedin && { id: "linkedin", href: branding.social_linkedin, Icon: LinkedinLogo },
    branding?.social_email   && { id: "email",    href: `mailto:${branding.social_email}`, Icon: EnvelopeSimple },
  ].filter(Boolean);

  return (
    <footer
      id="contact"
      data-testid="footer-section"
      className="bg-stone-900 text-stone-300 py-20 lg:py-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <div className="mb-5">
              <BrandLogo
                size={36} iconSize={20}
                markClassName="bg-emerald-600"
                nameClassName="font-heading text-lg font-semibold text-white tracking-tight"
              />
            </div>
            <p className="font-body text-sm text-stone-400 leading-relaxed max-w-xs mb-6" data-testid="footer-tagline">
              {tagline}
            </p>
            <div className="flex items-center gap-3">
              {socials.map((s) => (
                <a key={s.id} href={s.href} target="_blank" rel="noopener noreferrer"
                   data-testid={`footer-social-${s.id}`}
                   className="w-10 h-10 rounded-full bg-stone-800 hover:bg-emerald-700 flex items-center justify-center transition-colors duration-300">
                  <s.Icon size={18} weight="bold" className="text-stone-300" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(groups).map(([heading, links]) => (
            <div key={heading}>
              <h4 className="font-heading text-sm font-semibold text-white mb-4 tracking-wide">
                {heading}
              </h4>
              <ul className="space-y-3">
                {links.map((link, idx) => (
                  <li key={`${link.label}-${idx}`}>
                    <FooterLink link={link} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-stone-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-body text-xs text-stone-500">
            &copy; {new Date().getFullYear()} Conexus Foundation. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" data-testid="footer-link-privacy" className="font-body text-xs text-stone-500 hover:text-stone-300 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" data-testid="footer-link-terms" className="font-body text-xs text-stone-500 hover:text-stone-300 transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
